import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { BadgeIndianRupee, CalendarClock, Gift, CheckCircle2, AlertTriangle, RefreshCw, Clock } from 'lucide-react';
import {
  DURATION_OPTIONS, monthlyBaseForOrg, priceForDuration, isPlanExpired,
  daysUntilExpiry,
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';
import { fetchWithAuth } from '../../utils/api';

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
};

const PRETTY_DURATION = {
  week: '1 Week',
  month: '1 Month',
  '6month': '6 Months',
  year: '1 Year',
};

const Stat = ({ label, value, tone = 'default' }) => {
  const colors = {
    default: { fg: '#0d0d0d', wash: 'transparent' },
    success: { fg: '#047857', wash: 'rgba(16,185,129,0.08)' },
    warning: { fg: '#b45309', wash: 'rgba(245,158,11,0.10)' },
    danger: { fg: '#be123c', wash: 'rgba(244,63,94,0.08)' },
  };
  const c = colors[tone] || colors.default;
  return (
    <div className="rounded-lg p-3 border border-[var(--theme-tertiary)]" style={{ background: c.wash }}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{label}</p>
      <p className="text-[14px] font-extrabold mt-0.5" style={{ color: c.fg }}>{value}</p>
    </div>
  );
};

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  tone: PropTypes.oneOf(['default', 'success', 'warning', 'danger']),
};

// In-dashboard view of the current plan + renewal controls.
// Reuses the same /org/resubscribe endpoint as the locked-out page.
export const SubscriptionTab = ({ org, onRenewed, showNotification }) => {
  useSharedStore();
  const expired = isPlanExpired(org);
  const daysLeft = daysUntilExpiry(org);
  const [pricing, setPricing] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(org?.planDuration || 'month');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchWithAuth('/pricing');
        if (cancelled || !res.ok) return;
        const data = await res.json();
        const parse = (v) => {
          if (!v) return {};
          if (typeof v === 'string') {
            try { return JSON.parse(v); } catch { return {}; }
          }
          return v;
        };
        setPricing({ manualPrices: parse(data.manualPrices), tierPrices: parse(data.tierPrices) });
      } catch (e) {
        console.error(e);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const monthlyBase = useMemo(
    () => monthlyBaseForOrg({ pricing, orgType: org?.type, modules: org?.modules }),
    [pricing, org],
  );

  const renew = async () => {
    if (!selectedDuration || submitting) return;
    const amount = priceForDuration(monthlyBase, selectedDuration);
    setSubmitting(true);
    try {
      const res = await fetchWithAuth('/org/resubscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ planDuration: selectedDuration, planAmount: amount }),
      });
      if (res.ok) {
        const updated = await res.json();
        if (showNotification) showNotification('Plan renewed', 'success');
        if (onRenewed) onRenewed(updated);
      } else {
        const err = await res.json().catch(() => ({}));
        if (showNotification) showNotification(err.error || 'Failed to renew', 'error');
      }
    } catch (e) {
      console.error(e);
      if (showNotification) showNotification('Network error during renewal', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Status banner copy / tone
  let bannerIcon = CheckCircle2;
  let bannerTone = { bg: 'rgba(16,185,129,0.08)', fg: '#047857', border: 'rgba(16,185,129,0.25)' };
  let bannerText;
  if (!org?.planExpiresAt) {
    bannerText = 'No plan window set — renew below to start tracking your subscription.';
    bannerIcon = AlertTriangle;
    bannerTone = { bg: 'rgba(148,163,184,0.10)', fg: '#475569', border: 'rgba(148,163,184,0.3)' };
  } else if (expired) {
    bannerText = `Plan expired on ${fmtDate(org.planExpiresAt)}. Renew to restore full access for your team.`;
    bannerIcon = AlertTriangle;
    bannerTone = { bg: 'rgba(244,63,94,0.08)', fg: '#be123c', border: 'rgba(244,63,94,0.25)' };
  } else if (daysLeft <= 7) {
    bannerText = `Plan expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'} (${fmtDate(org.planExpiresAt)}). Renew now to avoid lockout.`;
    bannerIcon = AlertTriangle;
    bannerTone = { bg: 'rgba(244,63,94,0.06)', fg: '#be123c', border: 'rgba(244,63,94,0.20)' };
  } else if (daysLeft <= 30) {
    bannerText = `Plan expires in ${daysLeft} days (${fmtDate(org.planExpiresAt)}). Renew early to lock in current pricing.`;
    bannerIcon = Clock;
    bannerTone = { bg: 'rgba(245,158,11,0.08)', fg: '#b45309', border: 'rgba(245,158,11,0.25)' };
  } else {
    bannerText = `Plan active — ${daysLeft} days left (expires ${fmtDate(org.planExpiresAt)}).`;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Status banner */}
      <section className="rounded-lg px-4 py-3 border flex items-center gap-2 text-[12px] font-medium" style={{ background: bannerTone.bg, color: bannerTone.fg, borderColor: bannerTone.border }}>
        {(() => {
          const Icon = bannerIcon;
          return <Icon size={14} aria-hidden="true" />;
        })()}
        <span>{bannerText}</span>
      </section>

      {/* Stats grid */}
      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Current plan" value={PRETTY_DURATION[org?.planDuration] || '—'} />
        <Stat label="Last paid" value={`₹${Number(org?.planAmount || 0).toFixed(2)}`} />
        <Stat label="Started" value={fmtDate(org?.planStartedAt)} />
        <Stat
          label="Expires"
          value={fmtDate(org?.planExpiresAt)}
          tone={expired ? 'danger' : (daysLeft !== null && daysLeft <= 30) ? 'warning' : 'success'}
        />
      </section>

      {/* Renew card */}
      <section className="bg-white border border-[var(--theme-tertiary)] rounded-lg p-5 flex flex-col gap-4">
        <header className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 flex items-center gap-1.5">
            <CalendarClock size={11} aria-hidden="true" /> Renew plan
          </p>
          <p className="text-[11px] font-medium text-slate-500">
            Monthly base · <span className="font-extrabold text-[#0d0d0d]">₹{monthlyBase.toFixed(2)}</span>
          </p>
        </header>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {DURATION_OPTIONS.map((opt) => {
            const total = priceForDuration(monthlyBase, opt.id);
            const active = selectedDuration === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setSelectedDuration(opt.id)}
                aria-pressed={active}
                className={`text-left rounded-lg px-3 py-3 border transition-all ${active
                    ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)] shadow-sm'
                    : 'border-[var(--theme-tertiary)] hover:border-[var(--theme-primary-tint)] bg-white'
                  }`}
              >
                <div className="flex items-center justify-between">
                  <span className={`text-[12px] font-extrabold ${active ? 'text-[var(--theme-primary)]' : 'text-[#0d0d0d]'}`}>{opt.label}</span>
                  {opt.free && <Gift size={12} className={active ? 'text-[var(--theme-primary)]' : 'text-emerald-500'} aria-hidden="true" />}
                </div>
                <p className="text-[11px] font-medium text-slate-500 mt-0.5">{opt.tagline}</p>
                <p className="mt-1 text-[13px] font-extrabold text-[#0d0d0d]">
                  {opt.free ? <span className="text-emerald-600">Free</span> : <>₹{total.toFixed(2)}</>}
                </p>
              </button>
            );
          })}
        </div>

        <div className="flex items-center justify-end gap-3 pt-3 border-t border-[var(--theme-tertiary)]">
          <button
            type="button"
            onClick={renew}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-white text-xs font-semibold uppercase tracking-[0.14em] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'var(--theme-primary)' }}
          >
            {submitting
              ? <><RefreshCw size={12} className="animate-spin" aria-hidden="true" /> Renewing…</>
              : <>{expired ? 'Renew now' : 'Extend'} · {PRETTY_DURATION[selectedDuration]}</>}
          </button>
        </div>
      </section>
    </div>
  );
};

SubscriptionTab.propTypes = {
  org: PropTypes.object,
  onRenewed: PropTypes.func.isRequired,
  showNotification: PropTypes.func,
};
